import { reactive, computed } from "vue";
import {
  getPayPlanVersionDetailV2,
  voidPayPlanVersionV2,
  terminatePayPlanVersionV2,
} from "@/api/employeeAPI";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { apiErrorMessage } from "@components/ProposedPayPlanDrawer/composables/useProposalDrawer";
import { normalizeVersionStatus } from "@components/LapsedPayPlanDrawer/composables/useLapsedPayPlanDrawer";
import { flagcdnFromCode } from "@components/utils/countryFlagIcon";
import { resolveReadinessEnabled } from "@components/utils/useReadinessEnabled";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function toNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const PAY_BASIS_SHORT = {
  per_year: "Year",
  per_month: "Month",
  per_hour: "Hour",
  ANNUAL: "Year",
  MONTHLY: "Month",
  HOURLY: "Hour",
  Year: "Year",
  Month: "Month",
  Hour: "Hour",
};

function payBasisShort(basis) {
  return PAY_BASIS_SHORT[basis] || "Year";
}

function locationLabel(location) {
  if (!location) return "";
  if (typeof location === "string") return location;
  return (
    location.metro_area?.name ||
    location.region?.name ||
    location.local_unit?.name ||
    location.display_label ||
    location.name ||
    ""
  );
}

/**
 * Map the frozen "Fair Pay at effective date" block (Rule B-20) onto the
 * calculator card's shape. The API block is snake_case; the card reads
 * camelCase keys inside `pay_range_version`. `fallbackCountryCode` is the
 * version's operating country, used when the range carries no country.
 */
export function mapFairPayAtEffectiveDate(block, fallbackCountryCode = null) {
  if (!block) return null;
  const range = block.pay_range_version || null;
  const location = range?.location || null;
  return {
    status: block.fair_pay_status || null,
    readiness: block.readiness_pct ?? null,
    fair_increase: toNumber(block.fair_increase),
    position: block.position || null,
    pay_range_version: range
      ? {
          version: range.version_number ?? 1,
          asOfDate: formatFairPayDate(block.readiness_as_of || range.effective_from),
          code: range.code || "",
          jobCategory: range.job_category || "",
          level: range.level || "",
          currency: range.currency || "",
          payBasis: payBasisShort(range.pay_basis),
          location: locationLabel(location),
          countryFlag: flagcdnFromCode(
            location?.country_code || fallbackCountryCode
          ),
          anchors: range.anchors || null,
        }
      : null,
    adjusted_band: block.pay_range_adjusted || null,
  };
}

/**
 * Map the ER-12115 version detail onto the presentational
 * CurrentPayPlanDrawer props. The employee identity (name / title / avatar) is
 * not part of the detail payload yet, so the caller passes it in.
 */
export function mapVersionDetail(detail, employee = null) {
  if (!detail) return { version: null, fairPayAtEffectiveDate: null, rangePenetration: null };

  const currencyCode = detail.currency?.code || null;
  const countryCode = detail.country?.code || null;
  const fair = detail.fair_pay_at_effective_date || null;

  return {
    version: {
      code: detail.version_code || "",
      status: normalizeVersionStatus(detail.status),
      effective_from: detail.effective_from || null,
      effective_to: detail.effective_to || null,
      country: detail.country?.code || detail.country?.name || null,
      currency: currencyCode,
      amount: toNumber(detail.amount),
      pay_basis: detail.pay_basis || null,
      fte: detail.allocation_fte ?? null,
      employee: employee?.name || "",
      employee_title: employee?.title || "",
      avatar_url: employee?.avatarUrl || null,
      position_code: fair?.position?.code || null,
      position_name: fair?.position?.name || null,
    },
    fairPayAtEffectiveDate: mapFairPayAtEffectiveDate(fair, countryCode),
    rangePenetration: detail.range_penetration || null,
  };
}

/**
 * Wiring for the CurrentPayPlanDrawer (ER-12118).
 *
 * Loads a committed In Force / Current version's detail, maps it onto the
 * presentational drawer, and runs Terminate / Void. Propose Changes is bubbled
 * to the page (it re-enters the ER-12109 proposal flow). Mutations are gated
 * behind the Fair Pay MVP flag (idea-MDP-429). A 409 surfaces the server's own
 * message inline.
 */
export function useCurrentPayPlanDrawer({ proxy, fetchRows } = {}) {
  const state = reactive({
    open: false,
    loading: false,
    submitting: false,
    error: null,
    accountId: null,
    employeeId: null,
    versionId: null,
    detail: null,
    employee: null,
    futurePlanDate: null,
    mutationEnabled: false,
    terminateOpen: false,
    terminateDate: "",
    terminateReason: "",
    voidOpen: false,
    voidReason: "",
  });

  const mapped = computed(() => mapVersionDetail(state.detail, state.employee));
  const version = computed(() => mapped.value.version);
  const fairPayAtEffectiveDate = computed(
    () => mapped.value.fairPayAtEffectiveDate
  );
  /** Rule A-40: the detail's `readiness_enabled` is the account's Readiness product setting. */
  const readinessEnabled = computed(() => resolveReadinessEnabled(state.detail));
  const rangePenetration = computed(() => mapped.value.rangePenetration);
  const voidable = computed(() => state.detail?.voidable === true);
  const voidBlockedBy = computed(() => state.detail?.void_blocked_by || "");
  const terminable = computed(() => state.detail?.terminable === true);
  const futurePlanCode = computed(() => state.detail?.future_plan_code || null);
  const featureDisabled = computed(() => !state.mutationEnabled);

  /**
   * Rule B-18: the next version's effective date, named in the "change already
   * scheduled" note. The detail exposes the code but not the date yet, so the
   * page passes the scheduled version's start date from the summary.
   */
  const futurePlanDate = computed(
    () => state.detail?.future_plan_date || state.futurePlanDate || null
  );

  function notify(type, message) {
    const notifier = proxy?.$notifications?.[type];
    if (typeof notifier === "function") {
      notifier(message);
    } else if (type === "error") {
      state.error = message;
    }
  }

  function setInlineError(error, fallback) {
    state.error = apiErrorMessage(error, fallback);
  }

  async function ensureMutationEnabled() {
    try {
      state.mutationEnabled =
        (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_429, false)) === true;
    } catch {
      state.mutationEnabled = false;
    }
    return state.mutationEnabled;
  }

  async function loadDetail(versionId) {
    if (!state.accountId || versionId == null) return;
    state.loading = true;
    state.error = null;
    try {
      const response = await getPayPlanVersionDetailV2(state.accountId, versionId);
      state.detail = response?.data || null;
    } catch (error) {
      state.detail = null;
      setInlineError(error, "Could not load the pay plan version");
    } finally {
      state.loading = false;
    }
  }

  function open({ accountId, employeeId, versionId, employee, futurePlanDate } = {}) {
    state.open = true;
    state.error = null;
    state.accountId = accountId || getAccountId();
    state.employeeId = employeeId || null;
    state.versionId = versionId ?? null;
    state.detail = null;
    state.employee = employee || null;
    state.futurePlanDate = futurePlanDate || null;
    state.terminateOpen = false;
    state.terminateDate = "";
    state.terminateReason = "";
    state.voidOpen = false;
    state.voidReason = "";
    ensureMutationEnabled();
    loadDetail(state.versionId);
  }

  function close() {
    state.open = false;
    state.error = null;
    state.detail = null;
    state.employee = null;
    state.futurePlanDate = null;
    state.terminateOpen = false;
    state.terminateDate = "";
    state.terminateReason = "";
    state.voidOpen = false;
    state.voidReason = "";
  }

  function openTerminate() {
    state.terminateDate = state.detail?.effective_to || "";
    state.terminateReason = "";
    state.terminateOpen = true;
  }

  function cancelTerminate() {
    state.terminateOpen = false;
    state.terminateDate = "";
    state.terminateReason = "";
  }

  function openVoid() {
    state.voidReason = "";
    state.voidOpen = true;
  }

  function cancelVoid() {
    state.voidOpen = false;
    state.voidReason = "";
  }

  async function refresh() {
    if (fetchRows) await fetchRows();
  }

  async function runLifecycle(action, { successMessage, fallback }) {
    if (!state.mutationEnabled && !(await ensureMutationEnabled())) {
      notify("error", "Fair Pay pay plan actions are not available");
      return null;
    }

    state.submitting = true;
    state.error = null;
    try {
      await action();
      notify("success", successMessage);
      close();
      await refresh();
      return true;
    } catch (error) {
      setInlineError(error, fallback);
      notify("error", apiErrorMessage(error, fallback));
      return null;
    } finally {
      state.submitting = false;
    }
  }

  function terminateVersion() {
    const reason = state.terminateReason.trim();
    if (!reason) return Promise.resolve(null);
    return runLifecycle(
      () =>
        terminatePayPlanVersionV2(state.accountId, state.versionId, {
          termination_date: state.terminateDate || null,
          reason,
        }),
      {
        successMessage: `${version.value?.code || "Pay plan version"} terminated`,
        fallback: "Could not terminate the pay plan version",
      }
    );
  }

  function voidVersion() {
    const reason = state.voidReason.trim();
    if (!reason) return Promise.resolve(null);
    return runLifecycle(
      () => voidPayPlanVersionV2(state.accountId, state.versionId, { reason }),
      {
        successMessage: `${version.value?.code || "Pay plan version"} voided`,
        fallback: "Could not void the pay plan version",
      }
    );
  }

  return {
    state,
    version,
    fairPayAtEffectiveDate,
    readinessEnabled,
    rangePenetration,
    voidable,
    voidBlockedBy,
    terminable,
    futurePlanCode,
    futurePlanDate,
    featureDisabled,
    open,
    close,
    openTerminate,
    cancelTerminate,
    terminateVersion,
    openVoid,
    cancelVoid,
    voidVersion,
  };
}
